import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CelebrationsCard } from "@/components/CelebrationsCard";
import { CelebrationBanner } from "@/components/CelebrationBanner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme, isAdminRole } from "@/context/ThemeContext";
import { useDarkMode } from "@/components/ModeToggle";
import { useParentLang, LangSwitch } from "@/lib/parentLang";
import { CoordinatorSummary } from "@/components/CoordinatorSummary";
import { AppShell } from "@/components/AppShell";
import { Logo } from "@/components/Logo";
import { AuthImage } from "@/components/AuthImage";
import { Tile, Kpi, fmtDate, inr } from "@/components/ui-kit";
import { ClassPicker } from "@/components/ClassPicker";
import { DigestSendButton } from "@/components/DigestSendButton";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { TeacherDailyActivity } from "@/components/TeacherDailyActivity";

import { Bell, Hand, ChevronRight, ClipboardList, CalendarCheck, IndianRupee, NotebookPen, Star, Image, Megaphone, PartyPopper,
  MessageCircle, Users, TrendingUp, HeartHandshake, FileBarChart, UserCog, Wallet, BarChart3, BookOpen, Settings, Network,
  GraduationCap, Contact, CheckCircle2, Banknote, ListTodo, BookOpenText, Activity, SlidersHorizontal, Baby, Bus } from "lucide-react";

function Hero({ dark, photo, name, title, subtitle, unread }) {
  return (
    <div className={`hero-curve text-white px-5 sm:px-8 pt-5 pb-14 relative overflow-hidden ${dark ? "dark-surface" : "brand-header"}`}>
      <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-2xl" />
      <div className="flex items-start justify-between relative">
        <span className="w-8" />
        <Logo onDark size="md" stacked />
        <Link to="/notifications" data-testid="hero-bell" className="relative p-1.5"><Bell size={22} />{unread > 0 && <span className="absolute top-1 right-1 h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-white" />}</Link>
      </div>
      <div className="flex items-center gap-4 mt-5 relative max-w-5xl mx-auto">
        {photo ? <AuthImage src={photo} alt={name} className="h-16 w-16 sm:h-20 sm:w-20 rounded-full object-cover ring-4 ring-white/80" />
          : <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-white/20 ring-4 ring-white/70 flex items-center justify-center font-display text-2xl font-bold">{name?.charAt(0)}</div>}
        <div>
          <h1 data-testid="home-greeting" className="font-display text-2xl sm:text-3xl font-extrabold flex items-center gap-2">{title} <Hand size={22} className="text-amber-300" /></h1>
          {subtitle.map((s, i) => <div key={i} className={i === 0 ? "text-white/90 text-sm mt-0.5" : "text-white/70 text-xs mt-0.5"}>{s}</div>)}
        </div>
      </div>
    </div>
  );
}

const Body = ({ children }) => <div className="px-4 sm:px-8 -mt-8 relative max-w-5xl mx-auto space-y-5">{children}</div>;
const Grid = ({ children }) => <div className="grid grid-cols-3 gap-3 sm:gap-4">{children}</div>;

function ParentHome({ s, unread }) {
  const { user } = useAuth();
  const child = s.children?.[0];
  const u = s.latest_update;
  const { t } = useParentLang();
  return (
    <>
      <Hero photo={child?.photo_url} name={child?.name || user.name} unread={unread}
        title={`${t.hello}, ${(child?.name || user.name).split(" ")[0]}`}
        subtitle={child ? [`${t.cls}: ${child.class_name} - ${child.section}`, `${t.sid}: ${child.admission_number}`] : [t.noChild]} />
      <Body>
        <div className="flex justify-end"><LangSwitch /></div>
        <Grid>
          <Tile testid="tile-daily-update" to="/updates" icon={ClipboardList} label={t.tDaily} color="purple" badge={!!u && u.date === s.today} />
          <Tile testid="tile-child-diary" to="/my-diary" icon={BookOpenText} label={t.tDiary} color="purple" />
          <Tile testid="tile-my-child-day" to="/student-updates" icon={Baby} label={t.tDay} color="pink" />
          <Tile testid="tile-progress" to="/progress" icon={TrendingUp} label={t.tProgress} color="green" />
          <Tile testid="tile-attendance" to="/portal?tab=attendance" icon={CalendarCheck} label={t.tAtt} color="purple" />
          <Tile testid="tile-fees" to="/portal?tab=fees" icon={IndianRupee} label={t.tFees} color="yellow" />
          <Tile testid="tile-homework" to="/homework" icon={NotebookPen} label={t.tHw} color="purple" />
          <Tile testid="tile-activities" to="/updates?category=activity" icon={Star} label={t.tAct} color="orange" />
          <Tile testid="tile-photos" to="/gallery" icon={Image} label={t.tPhotos} color="blue" />
          <Tile testid="tile-announcements" to="/circulars" icon={Megaphone} label={t.tAnn} color="pink" />
          <Tile testid="tile-events" to="/events" icon={PartyPopper} label={t.tEvents} color="green" />
          <Tile testid="tile-messages" to="/messages" icon={MessageCircle} label={t.tMsg} color="purple" badge={unread > 0} />
          <Tile testid="tile-school-bus" to="/transport" icon={Bus} label={t.tBus} color="orange" />
        </Grid>
        <div>
          <div className="flex justify-between items-center mb-2 px-1">
            <h2 className="font-display font-bold text-slate-900 text-lg">{t.todayUpdate}</h2>
            <span className="text-xs font-semibold" style={{ color: "var(--brand)" }}>{fmtDate(s.today)}</span>
          </div>
          <Link to="/updates" data-testid="today-update-card" className="flex items-center gap-3 bg-white rounded-2xl p-3 shadow-sm">
            {u?.photo_url ? <AuthImage src={u.photo_url} alt="" className="h-14 w-20 rounded-xl object-cover" /> : <div className="h-14 w-14 rounded-xl bg-violet-50 flex items-center justify-center text-violet-500"><ClipboardList size={22} /></div>}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 truncate">{u ? u.title : t.noUpdates}</div>
              <div className="text-xs text-slate-500 truncate">{u ? `${u.category} • ${fmtDate(u.date)}` : t.teacherWillPost}</div>
            </div>
            <ChevronRight size={18} className="text-slate-400" />
          </Link>
        </div>
      </Body>
    </>
  );
}

function SetMyClass() {
  const { setUser, user } = useAuth();
  const [v, setV] = useState({ branch_id: "", class_name: "", section: "A" });
  async function save() {
    try { const { data } = await api.put("/me/class", { class_name: v.class_name, section: v.section }); setUser({ ...user, ...data }); toast.success("Your class is set"); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <div data-testid="set-my-class" className="bg-white rounded-2xl p-4 shadow-sm space-y-3">
      <div className="font-display font-bold text-slate-900">Choose your class</div>
      <ClassPicker value={v} onChange={setV} />
      <Button data-testid="save-my-class" onClick={save} disabled={!v.class_name} className="btn-brand">Save</Button>
    </div>
  );
}

function TeacherHome({ s, unread }) {
  const { user } = useAuth();
  const c = s.my_class;
  return (
    <>
      <Hero name={user.name} unread={unread} title={`Hello, ${user.name.split(" ")[0]}`} subtitle={[c ? `${c.class_name} - ${c.section} Teacher` : "Teacher", s.branch?.name || ""]} />
      <Body>
        <Grid>
          <Tile testid="tile-my-class" to="/students" icon={Users} label="My Class" color="purple" />
          <Tile testid="tile-daily-tasks" to="/tasks" icon={ListTodo} label="Daily Tasks" color="blue" />
          <Tile testid="tile-class-diary" to="/diary" icon={BookOpenText} label="Class Diary" color="purple" />
          <Tile testid="tile-student-updates" to="/student-updates" icon={Baby} label="Student Updates" color="pink" />
          <Tile testid="tile-progress" to="/progress" icon={TrendingUp} label="Progress Reports" color="green" />
          <Tile testid="tile-attendance" to="/attendance" icon={CalendarCheck} label="Attendance" color="blue" />
          <Tile testid="tile-daily-update" to="/updates" icon={ClipboardList} label="Daily Update" color="purple" />
          <Tile testid="tile-homework" to="/homework" icon={NotebookPen} label="Homework" color="purple" />
          <Tile testid="tile-activities" to="/gallery" icon={Star} label="Activities" color="orange" />
          <Tile testid="tile-messages" to="/messages" icon={MessageCircle} label="Messages" color="purple" badge={unread > 0} />
          <Tile testid="tile-progress" to="/gradebook" icon={TrendingUp} label="Student Progress" color="purple" />
          <Tile testid="tile-parent-connect" to="/messages" icon={HeartHandshake} label="Parent Connect" color="purple" />
          <Tile testid="tile-reports" to="/reports" icon={FileBarChart} label="Reports" color="purple" />
        </Grid>
        {c ? (
          <div>
            <div className="flex justify-between items-center mb-2 px-1">
              <h2 className="font-display font-bold text-slate-900 text-lg">Today's Class</h2>
              <span className="text-xs font-semibold" style={{ color: "var(--brand)" }}>{c.class_name} - {c.section}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Link to="/attendance" data-testid="today-class-attendance" className="bg-white rounded-2xl p-3 shadow-sm flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><CheckCircle2 size={20} /></div>
                <div><div className="text-xs text-slate-500">Attendance</div><div className="font-bold text-emerald-700">{c.present} / {c.students} Present</div></div>
              </Link>
              <Link to="/updates" data-testid="today-class-updates" className="bg-white rounded-2xl p-3 shadow-sm flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center"><ClipboardList size={20} /></div>
                <div><div className="text-xs text-slate-500">Updates</div><div className="font-bold text-amber-700">{c.updates_today} posted today</div></div>
              </Link>
            </div>
          </div>
        ) : <SetMyClass />}
        <TeacherDailyActivity />
      </Body>
    </>
  );
}

function PrincipalHome({ s, unread }) {
  const { user } = useAuth();
  return (
    <>
      <Hero name={user.name} unread={unread} title={`Hello, ${user.name.split(" ")[0]}`} subtitle={[s.branch ? `Branch: ${s.branch.name} (${s.branch.code})` : "", s.pending_leave ? `${s.pending_leave} leave request(s) waiting` : ""]} />
      <Body>
        <div className="grid grid-cols-4 gap-2 sm:gap-3">
          <Kpi testid="kpi-students" icon={Users} value={s.students} label="Students" tone="purple" />
          <Kpi testid="kpi-staff" icon={Contact} value={s.staff} label="Staff" tone="blue" />
          <Kpi testid="kpi-attendance" icon={CheckCircle2} value={s.attendance_pct == null ? "—" : `${s.attendance_pct}%`} label="Attendance" tone="green" />
          <Kpi testid="kpi-fees-month" icon={IndianRupee} value={inr(s.fees_month)} label="Fees (Month)" tone="orange" />
        </div>
        <CelebrationsCard />
        <Grid>
          <Tile testid="row-students" to="/students" icon={Users} label="Student Management" />
          <Tile testid="row-staff" to="/staff" icon={UserCog} label="Staff Management" color="blue" />
          <Tile testid="row-fees" to="/fees" icon={IndianRupee} label="Fees & Payments" color="yellow" />
          <Tile testid="row-attendance" to="/attendance" icon={CalendarCheck} label="Attendance Overview" color="green" />
          <Tile testid="row-academic" to="/gradebook" icon={BookOpen} label="Academic & Activities" color="orange" />
          <Tile testid="row-announcements" to="/circulars" icon={Megaphone} label="Announcements" color="pink" />
          <Tile testid="row-leave" to="/leave" icon={CalendarCheck} label="Leave Approvals" color="teal" badge={s.pending_leave > 0} />
          <Tile testid="row-messages" to="/messages" icon={MessageCircle} label="Messages" badge={unread > 0} />
          <Tile testid="row-reports" to="/reports" icon={BarChart3} label="Reports & Analytics" color="blue" />
          <Tile testid="row-monitor" to="/monitor" icon={Activity} label="Teacher Monitoring" color="purple" />
          <Tile testid="row-class-diary" to="/diary" icon={BookOpenText} label="Class Diaries" color="orange" />
          <Tile testid="row-tasks" to="/tasks" icon={ListTodo} label="Teacher Tasks" color="green" />
          <Tile testid="row-transport" to="/transport" icon={Bus} label="Transport" color="yellow" />
        </Grid>
      </Body>
    </>
  );
}

function CoordinatorHome({ s, unread }) {
  const { user } = useAuth();
  return (
    <>
      <Hero name={user.name} unread={unread} title={`Hello, ${user.name.split(" ")[0]}`} subtitle={["Coordinator", s.branch ? `Branch: ${s.branch.name} (${s.branch.code})` : "No branch assigned yet"]} />
      <Body>
        <div className="grid grid-cols-4 gap-2 sm:gap-3">
          <Kpi testid="kpi-students" icon={Users} value={s.students} label="Students" tone="purple" />
          <Kpi testid="kpi-attendance" icon={CheckCircle2} value={s.attendance_pct == null ? "—" : `${s.attendance_pct}%`} label="Attendance" tone="green" />
          <Kpi testid="kpi-diaries-pending" icon={BookOpenText} value={s.diaries_pending ?? 0} label="Diaries to review" tone="orange" />
          <Kpi testid="kpi-tasks-overdue" icon={ListTodo} value={s.tasks_overdue ?? 0} label="Overdue tasks" tone="red" />
        </div>
        <CoordinatorSummary />
        <Grid>
          <Tile testid="row-monitor" to="/monitor" icon={Activity} label="Teacher Monitoring" />
          <Tile testid="row-tasks" to="/tasks" icon={ListTodo} label="Teacher Tasks" color="green" />
          <Tile testid="row-class-diary" to="/diary" icon={BookOpenText} label="Class Diaries" color="orange" badge={s.diaries_pending > 0} />
          <Tile testid="row-assign" to="/diary-admin" icon={UserCog} label="Class Assignments" color="blue" />
          <Tile testid="row-attendance" to="/attendance" icon={CalendarCheck} label="Student Attendance" color="green" />
          <Tile testid="row-staff-attendance" to="/staff-attendance" icon={Contact} label="Staff Attendance" color="teal" />
          <Tile testid="row-homework" to="/homework" icon={NotebookPen} label="Homework" />
          <Tile testid="row-updates" to="/updates" icon={ClipboardList} label="Daily Updates" color="pink" />
          <Tile testid="row-events" to="/events" icon={PartyPopper} label="School Activities" color="orange" />
          <Tile testid="row-gallery" to="/gallery" icon={Image} label="Photos" color="blue" />
          <Tile testid="row-students" to="/students" icon={GraduationCap} label="Students" />
          <Tile testid="row-messages" to="/messages" icon={MessageCircle} label="Messages" badge={unread > 0} />
        </Grid>
      </Body>
    </>
  );
}

function TransportStaffHome({ unread }) {
  const { user } = useAuth();
  return (
    <>
      <Hero name={user.name} unread={unread} title={`Hello, ${user.name.split(" ")[0]}`} subtitle={["Transport Staff", "Start your trip and mark each student"]} />
      <Body>
        <Grid>
          <Tile testid="tile-my-trip" to="/transport" icon={Bus} label="My Trip" color="purple" />
          <Tile testid="tile-alerts" to="/notifications" icon={Bell} label="Notifications" color="orange" />
          <Tile testid="tile-notices" to="/notices" icon={Megaphone} label="Announcements" color="pink" />
        </Grid>
      </Body>
    </>
  );
}

function AdminHome({ s, unread, dark }) {
  const { user } = useAuth();
  const { theme } = useTheme();
  return (
    <>
      <Hero dark={dark} name={user.name} unread={unread} title={`Hello, ${user.name.split(" ")[0]}`} subtitle={[theme.school_name === "SPARKORA EDU" ? "Spark Family Enterprises" : theme.school_name]} />
      <Body>
        <div className="grid grid-cols-4 gap-2 sm:gap-3">
          <Kpi dark={dark} testid="kpi-students" icon={Users} value={s.students} label="Students" tone="purple" />
          <Kpi dark={dark} testid="kpi-staff" icon={Contact} value={s.staff} label="Staff" tone="blue" />
          <Kpi dark={dark} testid="kpi-collected" icon={IndianRupee} value={inr(s.fees_collected)} label="Collected" tone="green" />
          <Kpi dark={dark} testid="kpi-pending" icon={IndianRupee} value={inr(s.fees_pending)} label="Pending" tone="red" />
        </div>
        <Grid>
          <Tile dark={dark} testid="tile-branches" to="/branches" icon={Network} label="Branch & Users" />
          <Tile dark={dark} testid="tile-students" to="/students" icon={GraduationCap} label="Student Management" />
          <Tile dark={dark} testid="tile-payroll" to="/payroll" icon={Banknote} label="Staff & Payroll" />
          <Tile dark={dark} testid="tile-fees" to="/fees" icon={Wallet} label="Fees & Billing" color="yellow" />
          <Tile dark={dark} testid="tile-attendance" to="/attendance" icon={CalendarCheck} label="Attendance" />
          <Tile dark={dark} testid="tile-reports" to="/reports" icon={BarChart3} label="Reports & Analytics" />
          <Tile dark={dark} testid="tile-monitor" to="/monitor" icon={Activity} label="Teacher Monitoring" />
          <Tile dark={dark} testid="tile-diary-setup" to="/diary-admin" icon={SlidersHorizontal} label="Diary Setup" />
          <Tile dark={dark} testid="tile-class-diary" to="/diary" icon={BookOpenText} label="Class Diaries" />
          <Tile dark={dark} testid="tile-communication" to="/messages" icon={MessageCircle} label="Communication" badge={unread > 0} />
          <Tile dark={dark} testid="tile-academics" to="/gradebook" icon={GraduationCap} label="Academics" />
          <Tile dark={dark} testid="tile-transport" to="/transport" icon={Bus} label="Transport" />
          <Tile dark={dark} testid="tile-settings" to="/settings" icon={Settings} label="Settings" />
        </Grid>
        <div className="flex justify-end"><DigestSendButton /></div>
      </Body>
    </>
  );
}

export default function HomePage() {
  const { user } = useAuth();
  const darkMode = useDarkMode();
  const [s, setS] = useState(null);
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    api.get("/home/summary").then((r) => setS(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/messages/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
  }, [user?.class_name]);
  const admin = isAdminRole(user?.role);
  const props = { s: s || {}, unread, dark: admin && darkMode };
  return (
    <AppShell bare>
      <div data-testid="home-page" className="pb-8">
        {s && user.role !== "parent" && <CelebrationBanner />}
        {!s ? <div className="h-64 brand-header hero-curve animate-pulse" />
          : user.role === "parent" ? <ParentHome {...props} />
          : user.role === "teacher" ? <TeacherHome {...props} />
          : user.role === "coordinator" ? <CoordinatorHome {...props} />
          : user.role === "transport_staff" ? <TransportStaffHome {...props} />
          : admin ? <AdminHome {...props} /> : <PrincipalHome {...props} />}
      </div>
    </AppShell>
  );
}
